import { on } from './events'

interface TrackElementBoxOptions {
  /**
   * Also follow scrolling. Only needed by consumers that use `-bottom`, which is viewport-relative and
   * so changes on every scroll — the other two do not. Costs a layout read per animation frame while the
   * page is scrolling, so it is off unless asked for.
   */
  trackScroll?: boolean
}

/**
 * Mirrors one element's box into `<prefix>-bottom` / `-left` / `-width` custom properties on another, so
 * a `position: fixed` overlay can line up with it. Returns a destructor.
 *
 * Both listeners are needed: the observer catches the source being resized by something other than the
 * window, while a window resize can move the source sideways without changing its width at all — which
 * the observer does not report.
 */
export function trackElementBox(
  source: Element,
  target: HTMLElement,
  prefix: string,
  { trackScroll = false }: TrackElementBoxOptions = {},
): () => void {
  let last = ''
  let frame: number | null = null

  const sync = (): void => {
    const rect = source.getBoundingClientRect()
    const [bottom, left, width] = [rect.bottom, rect.left, rect.width].map(Math.round)

    if (`${bottom} ${left} ${width}` === last) {
      return
    }
    last = `${bottom} ${left} ${width}`

    target.style.setProperty(`${prefix}-bottom`, `${bottom}px`)
    target.style.setProperty(`${prefix}-left`, `${left}px`)
    target.style.setProperty(`${prefix}-width`, `${width}px`)
  }

  const syncOnNextFrame = (): void => {
    frame ??= requestAnimationFrame(() => {
      frame = null
      sync()
    })
  }

  sync()
  const observer = new ResizeObserver(sync)
  observer.observe(source)

  const destructors = [on(window, 'resize', sync)]
  if (trackScroll) {
    // Capturing, because a scroll event from a nested scroller does not bubble.
    destructors.push(on(window, 'scroll', syncOnNextFrame, { passive: true, capture: true }))
  }

  return () => {
    observer.disconnect()
    destructors.forEach((destructor) => destructor())
    if (frame !== null) {
      cancelAnimationFrame(frame)
    }
  }
}
